// M4-A §5 Services & logistics: contracted frac fleets / wireline / coiled tubing / flowback (day rate × frac-rate
// index, booking lead time), proppant (Northern White by rail vs in-basin, shortages), frac water sources & transfer,
// produced water & disposal (SWD wells, pipeline networks, recycling, trucking), induced seismicity & injection caps.
// State: state.shale.services (contracts), state.shale.water (facilities & water contracts) — lazy, ≥ 2000 only.
import { fracRateIndex, fracWaitDays, SAND, WATER, SEISMICITY, stepAt } from '../../data/m4/rules.js';
import { eqDef, fieldDef, stat, yf, isAvailable, unitPrice, eraDollars, defsByCat } from '../defs.js';
import { chance, range } from '../../core/rng.js';
import { notify, spend, nextId, fmt$, log } from '../util.js';
import * as markets from './markets.js';
import { SHALE_SIM, sh, fieldShale, activeCrewOf, ok, no, round } from './shaleUtil.js';

// ---- frac-rate index & lead time: the market workstream owns them (markets.fracRateIndex / serviceWaitDays);
// the rules lookups are the fallback.
export const fracIndexNow = (state) => markets.fracRateIndex?.(state) ?? fracRateIndex(yf(state));
export const fracWaitNow = (state) => markets.serviceWaitDays?.(state) ?? fracWaitDays(yf(state));

const svcKind = (def) => stat(def, 'service', null);
export const FRAC_SERVICES = ['frac', 'wireline', 'coiled_tubing', 'flowback'];

// Era-$ day rate of a completion service today (frac-fleet class × index; in-house crews shave the rate).
export function serviceDayRate(state, def) {
  let r = eraDollars(state, stat(def, 'dayRate', 0), stat(def, 'baseYear', def?.year ?? 2014));
  if (stat(def, 'dayRateClass', null) === 'frac_fleet') r *= fracIndexNow(state);
  const k = svcKind(def);
  if (k === 'frac') r *= 1 - Math.min(SHALE_SIM.fracCrewDiscountMax, SHALE_SIM.fracCrewDiscountPer * activeCrewOf(state, 'frac_crew').length);
  if (k === 'wireline' && activeCrewOf(state, 'wireline_operator').length) r *= 1 - SHALE_SIM.wirelineOpDiscount;
  return r;
}
export const fracServices = (state, fid, kind = null) => Object.values(state.shale?.services ?? {}).filter((c) => c.fieldId === fid && (!kind || c.kind === kind));
export const activeService = (state, c) => state.day >= c.startDay && (c.endDay == null || state.day < c.endDay);

function fieldHasGas(fid) {
  for (const p of fieldDef(fid)?.prospects ?? []) for (const r of p.reservoirs ?? []) if ((r.typeCurve?.streams?.gas ?? r.streams?.gas ?? 0) > 0) return true;
  return false;
}

// contractFracService(defId, { fieldId, days }) — frac fleet (booking lead time), wireline, coiled tubing, flowback.
export function contractFracServiceImpl(state, defId, { fieldId, days = null, auto = false } = {}) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'completion' || !FRAC_SERVICES.includes(svcKind(def))) return no('Not a completion service.');
  if (!isAvailable(def, state)) return no(`${def.name} is not available until ${def.year}.`);
  if (!state.fields[fieldId]) return no('Choose a field for the crew to work.');
  if (stat(def, 'needsTurbinePower', false) && !fieldHasGas(fieldId) && !Object.values(state.items).some((i) => i.defId === 'grid_electric'))
    return no(`${def.name} needs turbine power from field gas or a grid connection.`);
  const S = sh(state);
  const kind = svcKind(def);
  const lead = kind === 'frac' ? fracWaitNow(state) : 0;
  const uid = nextId(state, 'fs');
  const d = days != null ? Math.max(1, Math.round(days)) : null;
  S.services[uid] = { uid, defId, kind, fieldId, bookedDay: state.day, startDay: state.day + lead, endDay: d != null ? state.day + lead + d : null, auto: !!auto, billed: 0 };
  const rate = serviceDayRate(state, def);
  return ok(`${def.name} contracted at ${fieldDef(fieldId)?.name ?? fieldId}: ${fmt$(rate)}/day${d ? ` for ${d} days` : ''}${lead ? `, on location in ${lead} days (tight market)` : ''}.`, { uid });
}
export function releaseFracServiceImpl(state, uid) {
  const c = state.shale?.services?.[uid];
  if (!c) return no('No such contract.');
  const def = eqDef(c.defId);
  let fee = 0;
  if (c.endDay != null && state.day < c.endDay && !c.auto) { // early termination: 30% of the remaining term, capped at 30 days
    fee = Math.min(30, c.endDay - Math.max(state.day, c.startDay)) * serviceDayRate(state, def) * 0.3;
    if (fee > 0) spend(state, fee, 'other', false);
  }
  delete state.shale.services[uid];
  return ok(`${def?.name} released${fee > 0 ? ` (early termination ${fmt$(fee)})` : ''}.`);
}
// Daily: bill active contracts, end expired ones.
export function tickServices(state) {
  const S = state.shale;
  if (!S) return;
  for (const c of Object.values(S.services)) {
    if (c.endDay != null && state.day >= c.endDay) { delete S.services[c.uid]; log(state, `${eqDef(c.defId)?.name} contract at ${fieldDef(c.fieldId)?.name} ended.`); continue; }
    if (!activeService(state, c)) continue;
    const r = serviceDayRate(state, eqDef(c.defId)) * (c.busyDay === state.day - 1 || c.busyDay === state.day ? 1 : SHALE_SIM.standbyMult);
    spend(state, r, 'opex', false);
    c.billed += r;
  }
  for (const w of Object.values(S.water)) if (w.contract && w.endDay != null && state.day >= w.endDay) delete S.water[w.uid];
}

// ---- proppant ------------------------------------------------------------------------------------------------
export function inBasinAvailable(state, fid, def = eqDef('proppant_in_basin')) {
  const sd = fieldDef(fid)?.shale;
  if (!def || !isAvailable(def, state) || !sd) return false;
  if (yf(state) < (sd.sandSource?.inBasinFrom ?? 9999)) return false;
  return (stat(def, 'availableIn', []) ?? []).includes(fieldDef(fid)?.takeawayZone);
}
// Delivered $/ton of a proppant at a field (mine price + rail/transload/silo/truck logistics).
export function sandDeliveredPerTon(state, defId, fid) {
  const def = eqDef(defId);
  if (!def) return 0;
  const sd = fieldDef(fid)?.shale;
  const L = (x) => eraDollars(state, x, SAND.baseYear);
  let p = unitPrice(state, def);
  if (stat(def, 'inBasin', false)) p += L(SAND.truckPerTonMile * SHALE_SIM.inBasinTruckMi + SAND.lastMileSiloPerTon);
  else p += L(SAND.railPerTonMile * (sd?.sandSource?.haulMi ?? stat(def, 'railMiles', 1200)) + SAND.transloadPerTon + SAND.lastMileSiloPerTon + SAND.truckPerTonMile * SHALE_SIM.nwTruckMi);
  return p;
}
export function proppantOptions(state, fid) {
  return defsByCat('completion').filter((d) => svcKind(d) === 'proppant' && isAvailable(d, state) && (!stat(d, 'inBasin', false) || inBasinAvailable(state, fid, d)))
    .map((d) => ({ defId: d.id, name: d.name, perTon: round(sandDeliveredPerTon(state, d.id, fid), 2), meshMult: stat(d, 'meshMult', 1), inBasin: !!stat(d, 'inBasin', false), stockTons: round(state.stock[d.id]?.qty ?? 0, 1) }));
}
export function defaultProppant(state, fid) {
  if (inBasinAvailable(state, fid)) return 'proppant_in_basin';
  return 'proppant_northern_white';
}
export const sandShortageChance = (state) => stepAt(SAND.shortage, yf(state)) ?? 0;

// ---- water: facilities & contracts ------------------------------------------------------------------------------
const WATER_BUILT = new Set(['swd_well', 'brackish_water_well', 'water_recycling_plant']);
export function buildWaterFacilityImpl(state, defId, fieldId) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'water') return no('Not a water facility.');
  if (!WATER_BUILT.has(defId) && !(def.basePrice > 0)) return contractWaterServiceImpl(state, defId, fieldId);
  if (!isAvailable(def, state)) return no(`${def.name} is not available until ${def.year}.`);
  if (!fieldDef(fieldId)?.shale && !state.fields[fieldId]) return no('Choose a field.');
  const price = unitPrice(state, def);
  if (!spend(state, price, 'capex')) return no(`${def.name} costs ${fmt$(price)}; not enough cash.`);
  const S = sh(state);
  const uid = nextId(state, 'wf');
  const ready = state.day + stat(def, 'permitDays', 30);
  S.water[uid] = { uid, defId, kind: svcKind(def), fieldId, builtDay: state.day, readyDay: ready, contract: false, endDay: null };
  return ok(`${def.name} at ${fieldDef(fieldId)?.name}: ${fmt$(price)}, in service in ${ready - state.day} days.`, { uid });
}
export function contractWaterServiceImpl(state, defId, fieldId, { days = null, auto = false } = {}) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'water') return no('Not a water service.');
  if (WATER_BUILT.has(defId)) return buildWaterFacilityImpl(state, defId, fieldId);
  if (!isAvailable(def, state)) return no(`${def.name} is not available until ${def.year}.`);
  if (!state.fields[fieldId]) return no('Choose a field.');
  const S = sh(state);
  const uid = nextId(state, 'wf');
  S.water[uid] = { uid, defId, kind: svcKind(def), fieldId, builtDay: state.day, readyDay: state.day, contract: true, auto: !!auto, endDay: days != null ? state.day + Math.round(days) : null };
  return ok(`${def.name} contracted at ${fieldDef(fieldId)?.name}: ${fmt$(waterCostPerBbl(state, def, fieldId))}/bbl, ${stat(def, 'capacityBbld', 0).toLocaleString('en-US')} bbl/d.`, { uid });
}
export function releaseWaterImpl(state, uid) {
  const w = state.shale?.water?.[uid];
  if (!w) return no('No such water facility or contract.');
  delete state.shale.water[uid];
  return ok(`${eqDef(w.defId)?.name} ${w.contract ? 'released' : 'shut in and abandoned'}.`);
}
export const waterFacilities = (state, fid, kind = null) => Object.values(state.shale?.water ?? {}).filter((w) => w.fieldId === fid && state.day >= w.readyDay && (!kind || w.kind === kind));
export function droughtMult(state) {
  const y = Math.floor(yf(state));
  return (WATER.droughtYears.find(([yy]) => yy === y)?.[1]) ?? 1;
}
export function waterCostPerBbl(state, def, fid) {
  let c = eraDollars(state, stat(def, 'costPerBbl', 0), stat(def, 'baseYear', 2014));
  if (stat(def, 'droughtMult', null)) c *= droughtMult(state); // drought years: the rancher's price (WATER.droughtYears)
  if (svcKind(def) === 'transfer' && stat(def, 'roadDamage', false)) c *= 1 + (1 - seismicCap(state, fid)); // longer hauls under injection caps
  return c;
}
// Frac water supply today at a field: { capacity (bbl/d, Infinity on spot), costPerBbl (delivered, era $), spot }
export function fracWaterSupply(state, fid) {
  const fs = fieldShale(state, fid);
  const sources = waterFacilities(state, fid).filter((w) => w.kind === 'source' || w.kind === 'recycle');
  const transfer = waterFacilities(state, fid, 'transfer');
  const layflat = transfer.find((w) => stat(eqDef(w.defId), 'replacesTrucks', false));
  const transferCost = layflat ? waterCostPerBbl(state, eqDef(layflat.defId), fid) : eraDollars(state, SHALE_SIM.spotWaterTruckPerBbl, 2012);
  if (!sources.length) {
    if (!(state.shale?.auto?.water ?? true)) return { capacity: 0, costPerBbl: 0, spot: false };
    const ws = fieldDef(fid)?.shale?.waterSource;
    const fresh = /fresh/.test(ws?.kind ?? '');
    return { capacity: Infinity, costPerBbl: eraDollars(state, ws?.costPerBbl ?? 0.6, ws?.baseYear ?? 2015) * (fresh ? droughtMult(state) : 1), spot: true };
  }
  let cap = 0, cost = 0;
  for (const w of sources) {
    const d = eqDef(w.defId);
    const c = w.kind === 'recycle' ? Math.min(stat(d, 'capacityBbld', 0), fs.waterBbl ?? 0) : stat(d, 'capacityBbld', 0);
    cap += c; cost += c * waterCostPerBbl(state, d, fid);
  }
  return { capacity: cap, costPerBbl: (cap > 0 ? cost / cap : 0) + transferCost, spot: false };
}

// ---- disposal & seismicity ----------------------------------------------------------------------------------------
export function seismicCap(state, fid) {
  const y = yf(state);
  const rid = fieldDef(fid)?.regionId;
  let cap = 1;
  for (const o of SEISMICITY.orders) if (o.regionId === rid && y >= o.from && (!o.fieldIds || o.fieldIds.includes(fid))) cap = Math.min(cap, o.capMult);
  return cap;
}
// Disposal outlets at a field, cheapest first: [{ w, def, cap, costPerBbl, injects }]
export function disposalOutlets(state, fid) {
  const cap = seismicCap(state, fid);
  const out = [];
  for (const w of waterFacilities(state, fid)) {
    const d = eqDef(w.defId);
    if (w.kind === 'disposal') out.push({ w, def: d, cap: stat(d, 'capacityBbld', 0) * cap, costPerBbl: waterCostPerBbl(state, d, fid), injects: true });
    else if (w.kind === 'recycle') out.push({ w, def: d, cap: stat(d, 'capacityBbld', 0), costPerBbl: waterCostPerBbl(state, d, fid), injects: false });
    else if (w.kind === 'transfer' && stat(d, 'roadDamage', false)) out.push({ w, def: d, cap: stat(d, 'capacityBbld', 0), costPerBbl: waterCostPerBbl(state, d, fid) + eraDollars(state, SHALE_SIM.waterTruckThirdPartyFee, 2014), injects: false });
  }
  return out.sort((a, b) => a.costPerBbl - b.costPerBbl);
}
export const disposalCapacity = (state, fid) => disposalOutlets(state, fid).reduce((a, o) => a + o.cap, 0);

// Daily (after production): produced water → outlets; shortfall curtails tomorrow's production (NO_SWD); quakes.
export function tickWater(state) {
  const S = state.shale;
  if (!S) return;
  for (const [fid, fs] of Object.entries(S.fields)) {
    const W = fs.day === state.day ? fs.waterBbl : 0;
    if (W <= 0 && !waterFacilities(state, fid).length) { fs.curtail = 1; continue; }
    let outlets = disposalOutlets(state, fid);
    let cap = outlets.reduce((a, o) => a + o.cap, 0);
    if (W > cap + 1e-6 && (S.auto?.disposal ?? true) && fieldDef(fid)?.shale) { // auto: truck the shortfall to third-party SWDs
      const def = eqDef('water_trucking');
      const n = Math.ceil((W - cap) / Math.max(1, stat(def, 'capacityBbld', 15000)));
      for (let i = 0; i < n; i++) contractWaterServiceImpl(state, 'water_trucking', fid, { auto: true });
      if (n > 0) log(state, `${fieldDef(fid)?.name}: contracted ${n} water-trucking crew${n > 1 ? 's' : ''} to haul produced water to disposal.`);
      outlets = disposalOutlets(state, fid);
      cap = outlets.reduce((a, o) => a + o.cap, 0);
    }
    fs.disposalCap = Math.round(cap);
    let left = W, cost = 0, injected = 0;
    for (const o of outlets) {
      const v = Math.min(left, o.cap);
      if (v <= 0) break;
      left -= v; cost += v * o.costPerBbl;
      if (o.injects) { injected += v; quakeRoll(state, fid, o, v); }
    }
    if (cost > 0) spend(state, cost, 'opex', false);
    fs.injected = Math.round(injected);
    fs.waterCostToday = Math.round(cost);
    const curtail = W > 0 ? Math.min(1, cap / W) : 1;
    if (curtail < 0.999 && (fs.curtail ?? 1) >= 0.999) notify(state, 'problem', { fieldId: fid, problem: 'NO_SWD', level: 'warn', pause: true,
      text: `${fieldDef(fid)?.name}: no room for the produced water (${Math.round(W).toLocaleString('en-US')} bbl/d vs ${Math.round(cap).toLocaleString('en-US')} disposal) — wells are being shut in. Build an SWD, recycle, or truck it.` });
    fs.curtail = round(curtail, 4);
    // auto trucks no longer needed: send one home a day
    const spare = Object.values(S.water).find((w) => w.auto && w.fieldId === fid && w.defId === 'water_trucking');
    if (spare && cap - stat(eqDef('water_trucking'), 'capacityBbld', 15000) >= W * 1.1) delete S.water[spare.uid];
  }
}
function quakeRoll(state, fid, o, bbl) {
  const risk = fieldDef(fid)?.shale?.seismicityRisk ?? 0;
  const p = SEISMICITY.feltPerDayPer10kBbl * Math.pow(bbl / 10000, SEISMICITY.exponent) * risk * stat(o.def, 'seismicityMult', 1);
  if (!(p > 0) || !chance(state, p)) return;
  const damaging = chance(state, SEISMICITY.damagingShare);
  const mag = round(damaging ? range(state, 4.5, 5.8) : range(state, 3.0, 4.4), 1);
  const fs = fieldShale(state, fid);
  fs.quakes = (fs.quakes ?? 0) + 1; fs.lastQuakeDay = state.day;
  const S = sh(state);
  S.quakes.push({ day: state.day, fieldId: fid, mag, damaging, uid: o.w.uid });
  if (S.quakes.length > 50) S.quakes.splice(0, S.quakes.length - 50);
  state.company.stats.inducedQuakes = (state.company.stats.inducedQuakes ?? 0) + 1;
  if (damaging) {
    const fine = eraDollars(state, SEISMICITY.quakeFineBase, SEISMICITY.baseYear);
    spend(state, fine, 'other', false);
    state.company.reputation = Math.max(0, state.company.reputation - SEISMICITY.quakeRepHit);
    notify(state, 'seismicity', { fieldId: fid, mag, damaging: true, incident: 'induced_quake', level: 'warn', pause: true,
      text: `A magnitude ${mag} earthquake near your disposal well at ${fieldDef(fid)?.name} cracks chimneys in town. Regulators fine you ${fmt$(fine)}.` });
  } else {
    state.company.reputation = Math.max(0, state.company.reputation - SHALE_SIM.quakeFeltRep);
    notify(state, 'seismicity', { fieldId: fid, mag, damaging: false, level: 'info', text: `A magnitude ${mag} tremor is felt near your disposal well at ${fieldDef(fid)?.name}.` });
  }
}
export const quakeChancePerDay = (state, fid, bbl, defId = 'swd_well') =>
  SEISMICITY.feltPerDayPer10kBbl * Math.pow(bbl / 10000, SEISMICITY.exponent) * (fieldDef(fid)?.shale?.seismicityRisk ?? 0) * stat(eqDef(defId), 'seismicityMult', 1);
